import { IAssetsUrlFormatter } from '../helpers/assets-url.formatter';
import { IMediaContainer } from './IMediaContainer';
import { Log } from './Log';
import { ResourcePool } from './ResourcePool';
import { VideoData, VideoOption } from './VideoData';
import { VideoDataNull } from './VideoDataNull';
import { VideoResource } from './VideoResource';

export abstract class VideoResourcePool extends ResourcePool<VideoResource> {
  private readonly _type: string;

  private readonly pendingInit: Map<VideoResource, VideoData> = new Map();

  protected constructor(type: string, assetsUrlFormatter?: IAssetsUrlFormatter) {
    super(assetsUrlFormatter);
    this._type = type;
  }

  public async prepareFirstFrame(): Promise<void> {
    for (const [resource, data] of this.pendingInit) {
      try {
        await data.prepareToPlay();
      } catch (e) {
        Log.Error(`Video ${resource.id}: preinit failed: ${e}`);
      }
      resource.construct(data);
    }
    this.pendingInit.clear();
  }

  public get requiresMediaContainer(): boolean {
    return true;
  }

  public async loadResourceData(
    resource: VideoResource,
    xml: HTMLElement,
    container: IMediaContainer | null = null
  ): Promise<void> {
    const options: VideoOption[] = [];

    for (let i = 0; i < xml.childNodes.length; ++i) {
      const el = xml.childNodes[i] as HTMLElement;

      if (el.nodeName === this._type) {
        options.push({
          path: el.getAttribute('path')!,
          type: el.getAttribute('type')!,
        });
      }
    }

    return this.loadResourceDataByOptions(resource, options, container);
  }

  public async loadResourceDataByOptions(
    resource: VideoResource,
    options: VideoOption[],
    container: IMediaContainer | null = null
  ): Promise<void> {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.muted = true;
    video.autoplay = false;
    video.controls = false;
    video.loop = false;
    video.preload = 'metadata';
    video.hidden = true;
    video.style.display = 'none';

    return new Promise((resolve) => {
      const complete = async () => {
        video.removeEventListener('canplaythrough', complete);
        video.removeEventListener('error', onError);

        const videoData = this.createData(video);
        this.pendingInit.set(resource, videoData);
        resolve();
      };

      const onError = (e: Event) => {
        const targetElement = e.target as HTMLVideoElement;
        if (targetElement.error) {
          Log.Error(`Video: ${this._errorMessage(targetElement.error.code)}`);
        } else {
          Log.Error('Video: An unknown error occurred.');
        }

        video.removeEventListener('canplaythrough', complete);
        video.removeEventListener('error', onError);

        video.src = '';
        video.load();
        video.remove();

        resource.construct(new VideoDataNull());
        resolve();
      };

      video.addEventListener('canplaythrough', complete);
      video.addEventListener('error', onError);

      options.forEach((option) => {
        const source = document.createElement('source');
        source.src = this.formatUrl(option.path);
        source.type = option.type;

        if (source.src.endsWith('.mp4')) {
          video.insertBefore(source, video.firstChild);
        } else {
          video.appendChild(source);
        }
      });

      if (!container || !container.isValidContainer()) {
        document.body.appendChild(video);
      } else {
        container.appendToContainer(video);
      }

      video.load();
    });
  }

  public createResource(resoruceId: string): VideoResource {
    return new VideoResource(resoruceId);
  }

  public abstract createData(video: HTMLVideoElement): VideoData;

  public contextLost(): void {
    for (const v of this.resources.values()) {
      if (v.data) {
        v.data.reset();
      }
    }
  }

  private _errorMessage(code: number): string {
    switch (code) {
      case MediaError.MEDIA_ERR_ABORTED:
        return 'You aborted the video playback.';
      case MediaError.MEDIA_ERR_NETWORK:
        return 'A network error caused the video download to fail part-way.';
      case MediaError.MEDIA_ERR_DECODE:
        return 'The video playback was aborted due to a corruption problem or because the video used features your browser did not support.';
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        return 'The video could not be loaded, either because the server or network failed or because the format is not supported.';
      default:
        return 'An unknown error occurred.';
    }
  }
}
